// FIX: The `import * as React from 'react'` namespace import can cause issues with
// type resolution for class components in some build setups. Switching to the standard
// default import `import React from 'react'` ensures that `React.Component` is
// correctly identified, which makes `this.props` and `this.setState` available
// on the class instance as expected.
import React, { ErrorInfo } from 'react';
import { Link } from 'react-router-dom';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

// FIX: Switched to extending React.Component directly. This resolves the
// type inheritance issues where 'setState' and 'props' were not found on the class instance.
class ErrorBoundary extends React.Component<Props, State> {
  // The previous constructor-based approach was causing TypeScript compilation errors
  // related to `this.state` and `this.props`. This more modern syntax correctly
  // initializes state and resolves the type inheritance issues.
  state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // DEFINITIVE DEBUGGING FIX: Log the detailed error and component stack.
    // This is the most critical log for identifying the exact component that crashed.
    console.error("====================================================");
    console.error("ERROR BOUNDARY CAUGHT A RENDERING ERROR");
    console.error("====================================================");
    console.error("Error Message:", error.toString());
    console.error("Component Stack Trace:", errorInfo.componentStack);
    
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      // Display a detailed, developer-focused error message.
      return (
        <div className="text-left py-20 mx-auto max-w-4xl px-6">
          <h1 className="text-4xl font-bold font-serif text-red-400">Page Render Error</h1>
          <p className="mt-4 text-lg text-white/80">
            An unexpected error occurred while trying to render this page, likely due to malformed or missing data from the database. The detailed error message below has been logged to the console.
          </p>
          
          {this.state.error && (
            <div className="mt-6 p-4 rounded-lg bg-red-900/50 border border-red-500/50">
                <h2 className="text-xl font-semibold text-red-300">Error Details:</h2>
                <pre className="mt-2 text-red-200 text-sm whitespace-pre-wrap font-mono">
                  {this.state.error.toString()}
                </pre>
            </div>
          )}

          {this.state.errorInfo && (
            <div className="mt-4 p-4 rounded-lg bg-gray-800/50 border border-gray-500/50">
                <h2 className="text-xl font-semibold text-gray-300">Component Stack:</h2>
                <pre className="mt-2 text-gray-200 text-sm whitespace-pre-wrap font-mono">
                  {this.state.errorInfo.componentStack}
                </pre>
            </div>
          )}
          
          <div className="mt-8">
            <Link 
              to="/" 
              onClick={() => this.setState({ hasError: false, error: null, errorInfo: null })}
              // FIX: Removed duplicate focus:outline-none class
              className="inline-flex items-center justify-center gap-2 rounded-xl font-semibold text-center transition-colors duration-300 ease-in-out focus:ring-2 focus:ring-offset-2 focus:ring-offset-brand-dark gold-gradient-bg text-black px-6 py-3 text-base shadow-lg shadow-brand-gold/20 hover:brightness-110"
            >
              Go to Homepage
            </Link>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
